"use client";

import { ArrowRight, Flame, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "SIGNATURE",
  id: "signature"
}, {
  label: "SEASONAL",
  id: "seasonal"
}, {
  label: "BUNDLES",
  id: "bundles"
}, {
  label: "ACCESSORIES",
  id: "accessories"
}];
function FeaturedProductCard({
  product,
  index
}: {
  product: any;
  index: number;
}) {
  return <article data-index={index} className="group relative col-span-1 row-span-1 md:col-span-2 md:row-span-2 overflow-hidden border border-border bg-card">
      {}
      <div className="pointer-events-none absolute top-0 left-0 z-20 overflow-hidden w-40 h-40">
        <div className="absolute top-7 -left-10 w-48 text-center font-mono text-xs font-bold tracking-widest py-1.5 shadow-md" style={{
        transform: "rotate(-45deg)",
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)"
      }}>
          NEW BATCH
        </div>
      </div>

      {}
      <div className="pointer-events-none absolute inset-3 z-10">
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/60 transition-all duration-300 group-hover:inset-2" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/60" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary/60" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/60" />
      </div>

      {}
      <Link to={product.link} className="block relative">
        <div className="relative aspect-[4/5] md:aspect-auto md:h-[420px] lg:h-[520px] w-full overflow-hidden" style={{
        backgroundImage: "linear-gradient(135deg, var(--background) 0%, var(--card) 40%, hsla(0, 100%, 71%, 0.2) 100%)"
      }}>
          {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover p-8 transition-transform duration-500 group-hover:scale-105" />}

          {}
          <div className="pointer-events-none absolute inset-0 opacity-10" style={{
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 3px)"
        }} />
        </div>
      </Link>

      {}
      <div className="p-6 border-t border-border">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[11px] uppercase tracking-widest mb-2" style={{
            color: "var(--primary)"
          }}>
              Artisanal Selection
            </div>
            <h3 className="font-default text-2xl md:text-3xl font-bold leading-tight text-foreground">
              {product.title}
            </h3>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-mono text-2xl font-semibold text-foreground">
                ${Number(product.price ?? 0).toFixed(2)}
              </span>
              {product.discounted_price ? <span className="font-mono text-sm text-muted-foreground line-through">
                  ${Number(product.discounted_price).toFixed(2)}
                </span> : null}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <AddToCartButton productId={product.id} variant="default" size="lg" className="w-full" buttonClassName="bg-primary text-primary-foreground font-mono tracking-widest uppercase rounded-none" text="Add to Scoop" showQuantity={false} />
          </div>
        </div>
      </div>
    </article>;
}
function ProductCard({
  product,
  index
}: {
  product: any;
  index: number;
}) {
  const isLimited = product.tags?.some((t: any) => t.name?.toLowerCase().includes("limited")) ?? false;
  return <article data-index={index} className="group relative overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:border-primary/40">
      {}
      {isLimited && <div className="absolute top-3 left-3 z-20">
          <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-widest gap-1 px-2 py-1 rounded-lg bg-accent text-primary">
            <Flame className="w-3 h-3" />
            Small Batch
          </Badge>
        </div>}

      {}
      <Link to={product.link} className="block relative">
        <div className="relative aspect-square overflow-hidden bg-muted/30">
          {}
          <div className="pointer-events-none absolute inset-2 z-10">
            <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-primary/0 group-hover:border-primary transition-all duration-300 group-hover:top-2 group-hover:left-2" />
            <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-primary/0 group-hover:border-primary transition-all duration-300 group-hover:top-2 group-hover:right-2" />
            <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-primary/0 group-hover:border-primary transition-all duration-300 group-hover:bottom-2 group-hover:left-2" />
            <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-primary/0 group-hover:border-primary transition-all duration-300 group-hover:bottom-2 group-hover:right-2" />
          </div>

          {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        </div>
      </Link>

      {}
      <div className="p-4 space-y-2 relative">
        {product.categories && product.categories.length > 0 && <div className="font-mono text-[10px] uppercase tracking-widest" style={{
        color: "var(--primary)"
      }}>
            {product.categories[0].name}
          </div>}
        <Link to={product.link}>
          <h3 className="font-default text-base font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
            {product.title}
          </h3>
        </Link>

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-lg font-semibold text-foreground">
              ${Number(product.price ?? 0).toFixed(2)}
            </span>
            {product.discounted_price ? <span className="font-mono text-xs text-muted-foreground line-through">
                ${Number(product.discounted_price).toFixed(2)}
              </span> : null}
          </div>

          {}
          <div className="transition-all duration-300 sm:opacity-0 sm:translate-y-2 group-hover:opacity-100 group-hover:translate-y-0">
            <AddToCartButton productId={product.id} variant="default" size="icon" buttonClassName="bg-primary text-primary-foreground rounded-none" text="" showQuantity={false} />
          </div>
        </div>
      </div>
    </article>;
}
function ArmoryGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className={`rounded-none bg-card/50 border border-border animate-pulse ${i === 0 ? "sm:col-span-2 sm:row-span-2 h-[400px] lg:h-[640px]" : "aspect-[3/4]"}`} />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground font-mono text-sm">
        Unable to load our flavors.{" "}
        <Button variant="link" onClick={refetch} className="text-primary p-0 h-auto">
          Try again
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground font-mono text-sm uppercase tracking-widest">
        Our freezers are currently empty. Check back soon.
      </div>;
  }
  const [featured, ...rest] = products;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-auto">
      <FeaturedProductCard product={featured} index={0} />
      {rest.map((product, i) => <ProductCard key={product.id} product={product} index={i + 1} />)}
    </div>;
}
const wp_query_params = {
  per_page: 5,
  orderby: "date",
  order: "desc"
};
const wp_query = new WP_Query(wp_query_params);
export default function GiftSelection() {
  return <section data-section-id="613" id="gift-card-selection" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden dark">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} />

      <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 md:mb-16">
          <div className="flex-shrink-0">
            <div className="font-mono text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-2" style={{
            color: "var(--primary)"
          }}>
              <span className="inline-block w-8 h-px bg-primary" />
              Artisanal Collection
            </div>
            <h2 className="font-default text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.9] tracking-tight">
              <span className="block text-foreground">SCOOP /</span>
              <span className="relative inline-block">
                <span className="relative text-primary">
                  {}
                  <span aria-hidden="true" className="absolute -top-2 -left-1 w-4 h-4 border-t-4 border-l-4 border-background" />
                  SELECTION
                </span>
              </span>
            </h2>
          </div>

          {}
          <div className="flex flex-col gap-4 lg:items-end lg:max-w-xl w-full">
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-2 pb-3">
                {categories.map((cat, i) => <div key={cat.id} data-index={i} className="flex-shrink-0 px-4 py-2 border border-border bg-card font-mono text-xs uppercase tracking-widest text-foreground hover:border-primary hover:text-primary transition-colors cursor-default">
                    {cat.label}
                  </div>)}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>

            <Link to="/ShopPage" className="group inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-primary hover:gap-3 transition-all">
              View Full Menu
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 5,
        orderby: "date",
        order: "desc"
      }}>
          <ArmoryGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}